<template>
  <div>
    <b-container fluid>
      <h3 class="mt-3 mb-3">AML Bot</h3>

      <b-card class="mb-3">
        <h6 class="mb-3">AML Login Credentials</h6>
        <b-row v-for="cred in credentials" :key="cred.bot_name" class="mb-2 align-items-end">
          <b-col cols="12" md="2" class="mb-2 mb-md-0">
            <strong>{{ cred.label }}</strong>
          </b-col>
          <b-col cols="12" md="3" class="mb-2 mb-md-0">
            <b-form-group label="Username" class="mb-0">
              <b-form-input size="sm" v-model.trim="cred.username" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-2 mb-md-0">
            <b-form-group label="Password" class="mb-0">
              <b-form-input size="sm" type="password" v-model="cred.password" placeholder="Leave blank to keep unchanged" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="mb-2 mb-md-0">
            <b-button size="sm" variant="info" @click="saveCredential(cred)">Save</b-button>
          </b-col>
          <b-col cols="12" md="2">
            <small v-if="cred.updated_at" class="text-muted">Updated: {{ cred.updated_at }}</small>
          </b-col>
        </b-row>
      </b-card>

      <b-card class="mb-3">
        <h6 class="mb-3">Manual Trigger</h6>
        <b-button
          size="sm"
          variant="primary"
          class="mr-2"
          :disabled="triggering.home_delivery"
          @click="triggerBot('home_delivery', 'Home Delivery')"
        >
          {{ triggering.home_delivery ? "Triggering..." : "Run Home Delivery Bot Now" }}
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          :disabled="triggering.email_order"
          @click="triggerBot('email_order', 'Email Order')"
        >
          {{ triggering.email_order ? "Triggering..." : "Run Email Order Bot Now" }}
        </b-button>
      </b-card>

      <b-card class="mb-3">
        <h6 class="mb-3">Manual Upload</h6>
        <p class="text-muted small mb-3">
          If the OTP login or export email is broken, log into AsiaMiles by hand, download the export zip from the
          Home Delivery report screen, and upload it here with its zip password. This runs the same
          import / receive / commit / packing pipeline as the automated bot, except it does <strong>not</strong>
          upload tracking numbers back to AsiaMiles (that step needs a fresh login cookie) - the "AM Upload File"
          in the summary email still needs to be uploaded to AsiaMiles by hand.
        </p>
        <b-row>
          <b-col cols="12" md="4" class="mb-2 mb-md-0">
            <b-form-group label="Export Zip" class="mb-0">
              <b-form-file
                size="sm"
                v-model="manual_upload.file"
                accept=".zip"
                placeholder="Choose the AsiaMiles export .zip..."
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-2 mb-md-0">
            <b-form-group label="Zip Password" class="mb-0">
              <b-form-input size="sm" v-model.trim="manual_upload.zip_password" placeholder="From the export screen/email" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="mb-2 mb-md-0">
            <b-form-group label="Schedule Date" class="mb-0">
              <b-form-input size="sm" type="date" v-model="manual_upload.schedule_date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="1" class="mb-2 mb-md-0">
            <b-form-group label="Round" class="mb-0">
              <b-form-select size="sm" v-model="manual_upload.round" :options="[1, 2, 3]" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="d-flex align-items-end">
            <b-button size="sm" variant="primary" :disabled="manual_upload.submitting" @click="submitManualUpload">
              {{ manual_upload.submitting ? "Processing..." : "Process Upload" }}
            </b-button>
          </b-col>
        </b-row>

        <div v-if="manual_upload.result" class="mt-3">
          <b-alert :variant="manual_upload.result.status === 'success' ? 'success' : 'danger'" show>
            <div>{{ manual_upload.result.message }}</div>
            <div v-if="manual_upload.result.status === 'success'" class="small mt-1">
              Received: {{ manual_upload.result.order_receive_count }} |
              Committed: {{ manual_upload.result.order_commit_count }} |
              Added to Packing Queue: {{ manual_upload.result.packing_queue_count }} |
              Unmatched SKUs: {{ manual_upload.result.invalid_product_count }}
            </div>
          </b-alert>
          <details v-if="manual_upload.result.log && manual_upload.result.log.length">
            <summary class="text-muted small">View processing log</summary>
            <pre class="small mt-2">{{ manual_upload.result.log.join("\n") }}</pre>
          </details>
        </div>
      </b-card>

      <b-card class="mb-3">
        <h6 class="mb-3">Recent Runs</h6>
        <b-table responsive small outlined striped hover :items="logs" :fields="log_fields">
          <template v-slot:cell(bot_name)="data">
            {{ botLabel(data.item.bot_name) }}
          </template>
          <template v-slot:cell(status)="data">
            <b-badge :variant="runStatusVariant(data.item)">{{ runStatusText(data.item) }}</b-badge>
          </template>
        </b-table>
      </b-card>

      <b-card class="mb-3">
        <h6 class="mb-3">SF Override Period</h6>
        <p class="text-muted small mb-3">
          While an active period below covers today's date, every order processed by the AML bot skips automatic route
          assignment and packing (same as outlying-island districts today), so it falls into the manual
          <strong>SF Express</strong> handling export instead of the GOLS packing queue.
        </p>
        <b-row>
          <b-col cols="12" md="2" class="mb-2 mb-md-0">
            <b-form-group label="Start Date" class="mb-0">
              <b-form-input size="sm" type="date" v-model="sf_form.start_date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="mb-2 mb-md-0">
            <b-form-group label="End Date" class="mb-0">
              <b-form-input size="sm" type="date" v-model="sf_form.end_date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4" class="mb-2 mb-md-0">
            <b-form-group label="Reason" class="mb-0">
              <b-form-input size="sm" v-model.trim="sf_form.reason" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="d-flex align-items-end">
            <b-button size="sm" variant="info" @click="saveSfOverride">
              {{ sf_form.id ? "Update" : "Add" }}
            </b-button>
            <b-button v-if="sf_form.id" size="sm" variant="secondary" class="ml-2" @click="resetSfForm">
              Cancel
            </b-button>
          </b-col>
        </b-row>
        <b-form-checkbox class="mt-2" v-model="sf_form.active" switch>
          Active
        </b-form-checkbox>

        <b-table responsive small outlined striped hover class="mt-3" :items="sf_overrides" :fields="sf_fields">
          <template v-slot:cell(active)="data">
            <b-badge :variant="data.item.active == 1 ? 'success' : 'secondary'">
              {{ data.item.active == 1 ? "Active" : "Inactive" }}
            </b-badge>
          </template>
          <template v-slot:cell(actions)="data">
            <b-button size="sm" variant="outline-primary" @click="editSfOverride(data.item)">Edit</b-button>
            <b-button size="sm" variant="outline-danger" class="ml-1" @click="deleteSfOverride(data.item)">Delete</b-button>
          </template>
        </b-table>
      </b-card>

      <b-card class="mb-3">
        <h6 class="mb-3">Schedule Exceptions</h6>
        <b-row>
          <b-col cols="12" md="3" class="mb-2 mb-md-0">
            <b-form-group label="Bot" class="mb-0">
              <b-form-input size="sm" v-model.trim="form.bot_name" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="mb-2 mb-md-0">
            <b-form-group label="Date" class="mb-0">
              <b-form-input size="sm" type="date" v-model="form.schedule_date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="mb-2 mb-md-0">
            <b-form-group label="Action" class="mb-0">
              <b-form-select size="sm" v-model="form.action" :options="action_options" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-2 mb-md-0">
            <b-form-group label="Reason" class="mb-0">
              <b-form-input size="sm" v-model.trim="form.reason" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="d-flex align-items-end">
            <b-button size="sm" variant="info" @click="saveException">
              {{ form.id ? "Update" : "Add" }}
            </b-button>
            <b-button v-if="form.id" size="sm" variant="secondary" class="ml-2" @click="resetForm">
              Cancel
            </b-button>
          </b-col>
        </b-row>
        <b-form-checkbox class="mt-2" v-model="form.active" switch>
          Active
        </b-form-checkbox>
      </b-card>

      <b-pagination
        size="sm"
        :total-rows="total_rows"
        v-model="currentPage"
        :per-page="TABLE_LIMIT"
        @change="onPageChange"
        :limit="PAGING_LIMIT"
      />

      <b-table responsive small outlined striped hover :items="items" :fields="fields">
        <template v-slot:cell(action_type)="data">
          <b-badge :variant="data.item.action === 'skip' ? 'danger' : 'success'">
            {{ data.item.action === "skip" ? "Skip" : "Force Run" }}
          </b-badge>
        </template>
        <template v-slot:cell(active)="data">
          <b-badge :variant="data.item.active == 1 ? 'success' : 'secondary'">
            {{ data.item.active == 1 ? "Active" : "Inactive" }}
          </b-badge>
        </template>
        <template v-slot:cell(actions)="data">
          <b-button size="sm" variant="outline-primary" @click="editException(data.item)">Edit</b-button>
          <b-button size="sm" variant="outline-danger" class="ml-1" @click="deleteException(data.item)">Delete</b-button>
        </template>
      </b-table>
    </b-container>
  </div>
</template>

<script>
const fields = [
  { key: "bot_name", label: "Bot" },
  { key: "schedule_date", label: "Date" },
  { key: "action_type", label: "Action" },
  { key: "reason", label: "Reason" },
  { key: "active", label: "Status" },
  { key: "created_by_name", label: "Created By" },
  { key: "actions", label: "Actions" }
];

const log_fields = [
  { key: "bot_name", label: "Bot" },
  { key: "cronjob_start_at", label: "Started" },
  { key: "cronjob_end_at", label: "Ended" },
  { key: "status", label: "Status" }
];

const sf_fields = [
  { key: "start_date", label: "Start Date" },
  { key: "end_date", label: "End Date" },
  { key: "reason", label: "Reason" },
  { key: "active", label: "Status" },
  { key: "created_by_name", label: "Created By" },
  { key: "actions", label: "Actions" }
];

const bot_labels = {
  home_delivery: "Home Delivery",
  email_order: "Email Order",
  manual_upload: "Manual Upload"
};

export default {
  name: "BotScheduleExceptions",
  inject: ["API_HOST", "TABLE_LIMIT", "PAGING_LIMIT"],
  data() {
    return {
      items: [],
      fields,
      logs: [],
      log_fields,
      sf_overrides: [],
      sf_fields,
      sf_form: this.emptySfForm(),
      currentPage: 1,
      table_offset: 0,
      total_rows: 0,
      action_options: [
        { value: "skip", text: "Skip" },
        { value: "force_run", text: "Force Run" }
      ],
      form: this.emptyForm(),
      triggering: {
        home_delivery: false,
        email_order: false
      },
      manual_upload: {
        file: null,
        zip_password: "",
        schedule_date: "",
        round: 1,
        submitting: false,
        result: null
      },
      credentials: [
        { bot_name: "home_delivery", label: "Home Delivery", username: "", password: "", updated_at: null },
        { bot_name: "email_order", label: "Email Order", username: "", password: "", updated_at: null }
      ]
    };
  },
  created() {
    this.getExceptions();
    this.getCredentials();
    this.getLogs();
    this.getSfOverrides();
  },
  methods: {
    emptySfForm() {
      return {
        id: null,
        start_date: "",
        end_date: "",
        reason: "",
        active: true
      };
    },
    resetSfForm() {
      this.sf_form = this.emptySfForm();
    },
    async getSfOverrides() {
      const response = await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "aml_sf_override_get.php",
        data: { limit: 50, offset: 0 }
      });
      this.sf_overrides = response.data.overrides || [];
    },
    editSfOverride(item) {
      this.sf_form = {
        id: item.id,
        start_date: item.start_date,
        end_date: item.end_date,
        reason: item.reason || "",
        active: item.active == 1
      };
      window.scrollTo(0, 0);
    },
    async saveSfOverride() {
      if (!this.sf_form.start_date || !this.sf_form.end_date) {
        this.$eventHub.$emit("show_modal_message", {
          message_content: "Start date and end date are required.",
          variant: "danger"
        });
        return;
      }

      const response = await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "aml_sf_override_save.php",
        data: {
          ...this.sf_form,
          created_by: this.$store.state.user_obj.user_id
        }
      });

      if (response.data.status !== "success") {
        this.$eventHub.$emit("show_modal_message", {
          message_content: response.data.message || "Unable to save SF override period.",
          variant: "danger"
        });
        return;
      }

      this.resetSfForm();
      this.getSfOverrides();
    },
    async deleteSfOverride(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Delete SF override from ${item.start_date} to ${item.end_date}?`,
        { title: "Please Confirm", okVariant: "danger", centered: true }
      );
      if (!confirmed) return;

      await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "aml_sf_override_delete.php",
        data: { id: item.id }
      });
      this.getSfOverrides();
    },
    botLabel(bot_name) {
      return bot_labels[bot_name] || bot_name || "-";
    },
    runStatusText(item) {
      if (item.cronjob_end_at) return "Completed";
      const startedMs = new Date(item.cronjob_start_at.replace(" ", "T")).getTime();
      const staleAfterMs = 30 * 60 * 1000;
      return (Date.now() - startedMs) < staleAfterMs ? "Running" : "No end time recorded (possibly failed)";
    },
    runStatusVariant(item) {
      const text = this.runStatusText(item);
      if (text === "Completed") return "success";
      if (text === "Running") return "info";
      return "danger";
    },
    async getLogs() {
      const response = await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "aml_bot_log_get.php",
        data: { limit: 20 }
      });
      this.logs = response.data.logs || [];
    },
    async getCredentials() {
      const response = await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "aml_bot_credential_get.php"
      });
      const rows = response.data.credentials || [];
      this.credentials.forEach(cred => {
        const row = rows.find(r => r.bot_name === cred.bot_name);
        cred.username = (row && row.username) || "";
        cred.updated_at = (row && row.updated_at) || null;
      });
    },
    async saveCredential(cred) {
      if (!cred.username) {
        this.$eventHub.$emit("show_modal_message", {
          message_content: "Username is required.",
          variant: "danger"
        });
        return;
      }

      const response = await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "aml_bot_credential_save.php",
        data: {
          bot_name: cred.bot_name,
          username: cred.username,
          password: cred.password,
          updated_by: this.$store.state.user_obj.user_id
        }
      });

      this.$eventHub.$emit("show_modal_message", {
        message_content: response.data.message,
        variant: response.data.status === "success" ? "success" : "danger"
      });

      if (response.data.status === "success") {
        cred.password = "";
        this.getCredentials();
      }
    },
    async triggerBot(bot, label) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Trigger the ${label} bot to run now?`,
        { title: "Please Confirm", okVariant: "primary", centered: true }
      );
      if (!confirmed) return;

      this.triggering[bot] = true;
      try {
        const response = await this.$http({
          method: "POST",
          baseURL: `${this.API_HOST}`,
          url: "aml_bot_trigger.php",
          data: { bot }
        });

        this.$eventHub.$emit("show_modal_message", {
          message_content: response.data.message,
          variant: response.data.status === "success" ? "success" : "danger"
        });
        setTimeout(() => this.getLogs(), 3000);
      } finally {
        this.triggering[bot] = false;
      }
    },
    async submitManualUpload() {
      if (!this.manual_upload.file) {
        this.$eventHub.$emit("show_modal_message", {
          message_content: "Please choose the export zip file.",
          variant: "danger"
        });
        return;
      }
      if (!this.manual_upload.zip_password) {
        this.$eventHub.$emit("show_modal_message", {
          message_content: "Please enter the zip password.",
          variant: "danger"
        });
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        "Process this zip? This will import, receive, commit, and add matching orders to the packing queue.",
        { title: "Please Confirm", okVariant: "primary", centered: true }
      );
      if (!confirmed) return;

      this.manual_upload.submitting = true;
      this.manual_upload.result = null;
      try {
        const formData = new FormData();
        formData.append("zip_file", this.manual_upload.file);
        formData.append("zip_password", this.manual_upload.zip_password);
        formData.append("round", this.manual_upload.round);
        if (this.manual_upload.schedule_date) {
          formData.append("schedule_date", this.manual_upload.schedule_date);
        }
        formData.append("user_id", this.$store.state.user_obj.user_id);

        const response = await this.$http({
          method: "POST",
          baseURL: `${this.API_HOST}`,
          url: "aml_manual_import.php",
          headers: {
            "Content-Type": "multipart/form-data"
          },
          data: formData
        });

        this.manual_upload.result = response.data;
        this.getLogs();
      } catch (err) {
        this.manual_upload.result = {
          status: "error",
          message: (err.response && err.response.data && err.response.data.message) || "Upload failed."
        };
      } finally {
        this.manual_upload.submitting = false;
      }
    },
    emptyForm() {
      return {
        id: null,
        bot_name: "playwright_aml",
        schedule_date: "",
        action: "skip",
        reason: "",
        active: true
      };
    },
    resetForm() {
      this.form = this.emptyForm();
    },
    async getExceptions() {
      const response = await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "bot_schedule_exception_get.php",
        data: {
          offset: this.table_offset,
          limit: this.TABLE_LIMIT
        }
      });
      this.items = response.data.exceptions || [];
      this.total_rows = parseInt(response.data.total_rows || 0);
    },
    onPageChange(page) {
      this.table_offset = (page - 1) * this.TABLE_LIMIT;
      this.getExceptions();
    },
    editException(item) {
      this.form = {
        id: item.id,
        bot_name: item.bot_name,
        schedule_date: item.schedule_date,
        action: item.action,
        reason: item.reason || "",
        active: item.active == 1
      };
      window.scrollTo(0, 0);
    },
    async saveException() {
      if (!this.form.bot_name || !this.form.schedule_date) {
        this.$eventHub.$emit("show_modal_message", {
          message_content: "Bot and date are required.",
          variant: "danger"
        });
        return;
      }

      const response = await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "bot_schedule_exception_save.php",
        data: {
          ...this.form,
          created_by: this.$store.state.user_obj.user_id
        }
      });

      if (response.data.status !== "success") {
        this.$eventHub.$emit("show_modal_message", {
          message_content: response.data.message || "Unable to save schedule exception.",
          variant: "danger"
        });
        return;
      }

      this.resetForm();
      this.getExceptions();
    },
    async deleteException(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Delete ${item.bot_name} exception on ${item.schedule_date}?`,
        { title: "Please Confirm", okVariant: "danger", centered: true }
      );
      if (!confirmed) return;

      await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "bot_schedule_exception_delete.php",
        data: { id: item.id }
      });
      this.getExceptions();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/BotScheduleExceptions.vue